// src/pages/talentScout/allTalents.js

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import ProfilePicture from "../../components/profileImage";
import { Link } from "react-router-dom";
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const AllTalents = () => {
  const { t, i18n } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    updatePageTitle(t("talentScout.allTalents"), t("talentScout.talentDiscovery"));
  }, [updatePageTitle, t]);

  useEffect(() => {
    fetchPlayers();
  }, [search, gender, type, page]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        search,
        gender,
        type,
        page,
        limit,
      });

      const response = await fetch(
        `${API_URL}/talent-scout/getAllUsersAndTeams?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPlayers(data.players || data.results || []);
      // Assuming the API returns total count
      setTotalPages(Math.ceil((data.total || 0) / limit));
    } catch (error) {
      console.error("Error fetching players:", error);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const PlayerCard = ({ player }) => (
    <Link to={`/talent-scout/get-${player.accountType || "team"}/${player._id}`}>
      <div className="bg-white rounded-lg shadow-md p-6 flex flex-col h-full">
        <div className="flex items-center space-x-4 mb-4">
          {(player.type === "team" || player.accountType === "team") ? (
            <div className="w-16 h-16 rounded-full overflow-hidden">
              <img
                src={player.logo}
                alt={`${player.name}'s logo`}
                className="w-full h-full object-cover rounded-full"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-full h-full flex items-center justify-center bg-blue-500 text-white font-semibold text-sm"
                style={{ display: 'none' }}
              >
                {player.name?.charAt(0)?.toUpperCase() || 'T'}
              </div>
            </div>
          ) : (
            <ProfilePicture
              image={player.profilePicture || player.logo}
              myUser={player}
              alt={`${player.firstName || player.name}'s profile`}
              className="w-16 h-16 rounded-full object-cover"
            />
          )}
          <div>
            <h3 className="text-lg font-semibold">
              {player.firstName} {player.lastName} {player.name}
            </h3>
            <p className="text-gray-600">
              @{player.userName || player.uniqueName}
            </p>
            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
              {player.type || player.accountType}
            </span>
          </div>
        </div>
        <div className="space-y-2 text-gray-700">
         
          {player.gender && <p>{t("gender")}: {t(player.gender)}</p>}
        </div>
      </div>
    </Link>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-6">{t("talentScout.allTalents")}</h1>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t("talentScout.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2">🔍</span>
          </div>

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
          >
            <option value="">{t("talentScout.allTypes")}</option>
            <option value="player">{t("talentScout.players")}</option>
            <option value="organization">{t("talentScout.organizations")}</option>
            <option value="team">{t("talentScout.teams")}</option>
          </select>

          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
          >
            <option value="">{t("teams.allGenders")}</option>
            <option value="male">{t("male")}</option>
            <option value="female">{t("female")}</option>
            <option value="other">{t("other")}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-600 text-lg py-8">{t("loading")}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
            {players.map((player) => (
              <PlayerCard key={player._id} player={player} />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8 flex justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`px-4 py-2 rounded-lg ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {t("previous")}
            </button>

            <div className="flex items-center gap-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setPage(i + 1)}
                  className={`w-10 h-10 rounded-lg ${
                    page === i + 1
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`px-4 py-2 rounded-lg ${
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {t("next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AllTalents;
